import * as CertificationWatermark from '@tableau/certification-watermark';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import _ from 'underscore';

import { Formatters, Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { filePathDisplayStringWithBaseNameFirst } from '../../../common/utilities/FilePathUtils';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { L10n } from '../../../ts/L10n/L10n';
import { isRemote } from '../../../ts/models/ConnectionDetailInfo';
import { IDataConnectionInfo } from '../../../ts/models/IDataConnectionInfo';
import { IDataSourceBase, IDataSourceDetailInfo, IDataSourceInfo } from '../../../ts/models/IDataSourceInfo';
import { DataConnections } from '~src/connectivity/resources/DataConnections';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ILink, LinkWithCount } from './dataGridCells/LinkCell';
import { ITextWithIconSpecifiedByUrl, PlainTextCellWithIconAndCount } from './dataGridCells/PlainTextAndNumberCells';
import { CertificationMarkTooltipCell } from './dataGridCells/CertificationMarkTooltipCell';

export namespace ConnectionHoverPanel {

  interface TitleProps {
    count: number;
  }

  interface DetailRowProps {
    data: ConnectionDisplay.Metadata;
  }

  export function shouldDisplayPanel(datasource: IDataSourceInfo) {
    return datasource.dataConnectionsCount > 1;
  }

  function PopoverTitleElement(): React.StatelessComponent<TitleProps> {
    return function PopoverTitle(props: TitleProps) {
      const connectionCountString = Messages.connectionsCount({ COUNT: props.count,  COUNT_LOC: Formatters.formatCount(props.count) });
      return <div className="tb-content-item-popover-title">{ connectionCountString }</div>;
    };
  }

  // This is used for the data inside the tooltip from a connectsTo cell
  function ConnectionDetailRowElement(): React.StatelessComponent<DetailRowProps> {

    return function DataConnectionDetailRow(props: DetailRowProps) {
      const data = props.data;
      const text = ConnectionDisplay.translatedText(data);
      const rowHeight = CommonStyles.Sizes.CompactRowHeight;

      const ConnectionNameElement = () => data.href
        ? <a href={ data.href }>{ text.connectionName }</a>
        : <span>{ text.connectionName }</span>;

      const ConnectionTypeElement = () =>
        <span style={ { color: CommonStyles.Colors.Grey153} }>{ text.connectionTypeInParentheses }</span>;

      const rowStyle = _.extend({},
        CommonStyles.Layout.TextEllipsis, {
          height: rowHeight,
          lineHeight: rowHeight + 'px',
          cursor: 'default'
        });
      const iconStyle = { ...ExplorerStyles.connectionIconStyle, verticalAlign:'text-bottom'};

      /* eslint-disable jsx-a11y/alt-text */
      return (
        <div style={ rowStyle }>
          <img src={props.data.iconImageUrl} title={ text.connectionType } style={iconStyle} />
          <span title={ text.tooltip }>
            <ConnectionNameElement />&nbsp;<ConnectionTypeElement />
          </span>
        </div>
      );
    };
  }

  export function getPopoverContent(siteUrls: SpacesSiteUrls): (props: BodyCell.Props) => Promise<JSX.Element> {
    const ConnectionDetailRow = ConnectionDetailRowElement();
    const PopoverTitle = PopoverTitleElement();

    // This helper function returns one row of the popover content.
    function makeConnectionDetailRow(connection: IDataConnectionInfo) {
      const metadata = ConnectionDisplay.getDisplayMetadata(connection, siteUrls);
      return <ConnectionDetailRow data={ metadata } key={ connection.id } />;
    }

    // Return a function that, given the cell props, fetches data and returns the popover content.
    return (props: BodyCell.Props) => {
      return new Promise<JSX.Element>((resolve, reject) => {
        DataConnections.getFromDatasource(props.row.data as IDataSourceInfo)
          .then(connections => {
            resolve(
              <div data-tb-test-id="popover-menu-panel">
                <PopoverTitle count={connections.length} />
                <div style={{ marginTop: CommonStyles.Sizes.DefaultSpace }}>
                  { _.map(connections, makeConnectionDetailRow) }
                </div>
              </div>
            );
          })
          .catch(error => reject(error));
      });
    };
  }
}

export namespace ConnectionDisplay {

  export interface Metadata {
    connectionName: string;
    connectionType: string;
    href?: string;
    iconImageUrl: string;
    cardIconUrl: string;
    iconTitle?: string;
  }

  export interface TranslatedText {
    connectionName: string;
    nameUnavailable: boolean;
    connectionType: string;
    connectionTypeInParentheses: string;
    tooltip: string;
    iconTitle?: string;
  }

  export function translatedText(data: Metadata): TranslatedText {
    const connectionNameText = data.connectionName || Messages.filenameUnavailable();
    const connectionTypeText = Messages.parenthesizedArgument({ ITEM: data.connectionType});

    return {
      connectionName: connectionNameText,
      nameUnavailable: !data.connectionName,
      connectionType: data.connectionType,
      connectionTypeInParentheses: connectionTypeText,
      tooltip:  connectionNameText + ' ' + connectionNameText,
      iconTitle: data.iconTitle? data.iconTitle:data.connectionType
    };
  }

  export function getDisplayMetadata(connection: IDataSourceInfo | IDataConnectionInfo,
                                     siteUrls: SpacesSiteUrls): Metadata {
    const details = connection.connectionDetails;
    if (!details) {
      return null;
    }

    switch (details.type) {
      case 'tableauserver':
        const hasExtract = (connection as IDataSourceBase).publishedDatasourceDetails ? (connection as IDataSourceBase).publishedDatasourceDetails.hasExtracts: false;
        return {
          connectionName: details.name,
          connectionType: connection.connectionTypeDisplayName,
          href: siteUrls.exploreDataConnectionUrl(details.id),
          iconImageUrl: hasExtract? imgPath('datasource_extract_14px_small.svg'):imgPath('tableau_server_connection_type_small.svg'),
          cardIconUrl: hasExtract? imgPath('datasource_extract_60px_card.svg') : imgPath('tableau_server_connection_type_card.svg'),
          iconTitle: Messages.column_connects_to_icon_title_ds({LIVE_OR_EXTRACT:hasExtract? Messages.extract() : Messages.live()})
        };
      case 'file':
        return {
          connectionName: filePathDisplayStringWithBaseNameFirst(details.fileName),
          connectionType: connection.connectionTypeDisplayName,
          iconImageUrl: imgPath('file_connection_type_small.svg'),
          cardIconUrl: imgPath('file_connection_type_card.svg'),
          iconTitle: Messages.column_connects_to_icon_title_file({FILE_TYPE:connection.connectionTypeDisplayName})
        };
      case 'tableauserversite':
          return {
            connectionName: details.siteUrl,
            connectionType: connection.connectionTypeDisplayName,
            iconImageUrl: imgPath('external_server_connection_type_small.svg'),
            cardIconUrl: imgPath('external_server_connection_type_card.svg')
          };
      default:
        if (isRemote(details)) {
          return {
            connectionName: details.serverName,
            connectionType: connection.connectionTypeDisplayName,
            iconImageUrl: imgPath('external_server_connection_type_small.svg'),
            cardIconUrl: imgPath('external_server_connection_type_card.svg'),
            iconTitle: Messages.column_connects_to_icon_title_db({DB_TYPE:connection.connectionTypeDisplayName})
          };
        } else {
          return null;
        }
    }
  }
}

export namespace ConnectsToCell {

  export interface Props extends BodyCell.Props {
    siteUrls: SpacesSiteUrls;
    getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>;
    hideTooltips?: (data: IDataSourceInfo | IDataConnectionInfo) => boolean;
  }

  export const Cell = (props: ConnectsToCell.Props): JSX.Element => {
    const dataConnection = props.row.data as IDataSourceInfo | IDataConnectionInfo;
    const metadata = ConnectionDisplay.getDisplayMetadata(dataConnection, props.siteUrls);

    if (!metadata) {
      return null;
    }

    const translatedText = ConnectionDisplay.translatedText(metadata);
    let additionalConnectionsDisplay: string | undefined = undefined;

    if ((dataConnection as IDataSourceInfo).dataConnectionsCount) {
      const count = (dataConnection as IDataSourceInfo).dataConnectionsCount;
      additionalConnectionsDisplay = additionalConnectionsText(count);
    }

    if (metadata.href) {
      const cellData: (row: DataGrid.Row) => ILink = () => {
        return {
          text: translatedText.connectionName,
          title: translatedText.tooltip,
          url: metadata.href,
          iconUrl: metadata.iconImageUrl,
          iconTitle: translatedText.iconTitle? translatedText.iconTitle:translatedText.connectionType,
          style: translatedText.nameUnavailable ? MissingNameStyle : null,
          hideTooltips: true
        };
      };

      return <LinkWithCount
        {
          ...{
            ...props,
            ...props.siteUrls,
            linkFn: cellData,
            additionalConnectionsDisplay: props.getPopoverContent,
            getPopoverContent: props.getPopoverContent
          }
        }
      />;

    } else {
      const cellData: ITextWithIconSpecifiedByUrl = {
        text: translatedText.connectionName,
        title: translatedText.tooltip,
        textStyle: translatedText.nameUnavailable ? MissingNameStyle : null,
        iconUrl: metadata.iconImageUrl,
        iconTitle: translatedText.iconTitle? translatedText.iconTitle:translatedText.connectionType,
        hideTooltips: true
      };
      return <PlainTextCellWithIconAndCount
        {
          ... {
            ...props,
            ...props.siteUrls,
            metadataFn: () => cellData,
            getPopoverContent: props.getPopoverContent,
            count: additionalConnectionsDisplay
          }
        }
      />;
    }
  };

  const MissingNameStyle: React.CSSProperties = { fontStyle: 'italic', color: CommonStyles.Colors.Grey178 };

  export function cellFormatter(siteUrls: SpacesSiteUrls,
                                getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>,
                                hideTooltips?: (data: IDataSourceInfo | IDataConnectionInfo) => boolean
                              ): React.StatelessComponent<BodyCell.Props> {
    return function cell(props: BodyCell.Props): React.ReactElement<any> {
      return <ConnectsToCell.Cell {...{siteUrls, getPopoverContent, hideTooltips, ...props}} />;
    };
  }

  function additionalConnectionsText(connectionCount: number): string {
    if (connectionCount === 0 || connectionCount === 1) {
      return '';
    }

    return Messages.additionalConnectionsPillText(
      { CONNECTION_COUNT: L10n.localizedCount(connectionCount - 1) });
  }
}

export namespace CertifiableConnectsToCell {
  const WrapperStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-start'
  };

  const CertificationMarkIconStyle: React.CSSProperties = { flexShrink: 0 };

  const ConnectsToStyle: React.CSSProperties = { overflowX: 'hidden' };

  export function cellFormatter(
    isCertified: (row: DataGrid.Row) => boolean,
    getTooltipProps: (row: DataGrid.Row) => CertificationWatermark.CertificationTooltipProps,
    siteUrls: SpacesSiteUrls,
    getPopoverContent: (props: BodyCell.Props) => Promise<JSX.Element>,
    shouldDisplayPanel: (datasource: IDataSourceInfo) => boolean
  ): React.StatelessComponent<BodyCell.Props> {
    return function(props: BodyCell.Props) {
      return (
        <span style={WrapperStyle}>
          <span style={CertificationMarkIconStyle}>
            {React.createElement(CertificationMarkTooltipCell(isCertified, getTooltipProps), props)}
          </span>
          <span style={ConnectsToStyle}>
            <ConnectsToCell.Cell {...{siteUrls, getPopoverContent, hideTooltips: ConnectionHoverPanel.shouldDisplayPanel, ...props}} />
          </span>
        </span>
      );
    };
  }
}
